import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import { ep } from "@/api/endpoints";
import { qk } from "@/api/queryKeys";
import type { Bricks, Conversation, ModelInfo } from "@/api/types";
import { useAuthStore } from "@/stores/authStore";
import { useConfigStore } from "@/stores/configStore";

/** GET /api/conversations?plotId= get-or-create. staleTime 0(0.3절), 회원만. 응답 balance는 authStore.balance(단일 출처)로. */
export function useConversation(plotId: string, enabled: boolean) {
  const query = useQuery({
    queryKey: qk.conversation(plotId),
    queryFn: () => apiFetch<Conversation>(ep.conversations(plotId)),
    enabled: enabled && Boolean(plotId),
    staleTime: 0,
  });
  const balance = query.data?.balance;
  useEffect(() => {
    if (balance !== undefined) useAuthStore.getState().setBalance(balance);
  }, [balance, query.dataUpdatedAt]);
  return query;
}

/** GET /api/me/bricks → authStore.balance. 포그라운드 복귀 때 invalidate(ChatPage). */
export function useBricks(enabled: boolean) {
  const query = useQuery({
    queryKey: qk.bricks,
    queryFn: () => apiFetch<Bricks>(ep.meBricks),
    enabled,
  });
  const balance = query.data?.balance;
  useEffect(() => {
    if (balance !== undefined) useAuthStore.getState().setBalance(balance);
  }, [balance, query.dataUpdatedAt]);
  return query;
}

/** 0.3 파생: conversation.model ?? settings.defaultModel ?? pricing.models.find(isDefault) ?? models[0]. */
export function useCurrentModel(conversationModel: string | undefined): ModelInfo | undefined {
  const defaultModelId = useAuthStore((s) => s.settings?.defaultModel);
  const models = useConfigStore((s) => s.settings?.pricing.models);
  if (!models) return undefined;
  return models.find((m) => m.id === conversationModel) ?? models.find((m) => m.id === defaultModelId) ?? models.find((m) => m.isDefault) ?? models[0];
}

/** qk.conversation 캐시에 부분 패치(낙관 모델·프리셋·메시지 append). 캐시가 없으면 아무것도 하지 않는다. */
export function usePatchConversation(plotId: string) {
  const qc = useQueryClient();
  return (patch: (c: Conversation) => Conversation) => qc.setQueryData<Conversation>(qk.conversation(plotId), (old) => (old ? patch(old) : old));
}
